/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect, useMemo } from "react";
import { bool, func } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { goBack } from "react-router-redux";
import { actions as transferActions, selectors as transferSelectors } from "reducers/transfer";
import { actions as layoutActions } from "reducers/layout";

import { resizableRoute } from "pages/_components/Resizable";
import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";

import * as config from "util/config";
import * as i18n from "util/i18n";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { getTransferHistoricPath } from "util/v3UxConfig";
import {
    formatTransferDownloadDateLocal,
    getTransferDownloadDateWindow,
    getTransferDownloadLabels,
} from "v3/pages/transfers/transferDownload";

import { Box, Text, TransferDownload as TransferDownloadPage } from "@ui-kit/components/index.es";

function TransferDownload(props) {
    TransferDownload.propTypes = {
        dispatch: func.isRequired,
        isDesktop: bool.isRequired,
        fetching: bool.isRequired,
        embeddedInV3Shell: bool,
    };

    TransferDownload.defaultProps = {
        embeddedInV3Shell: false,
    };

    const { isDesktop, fetching, embeddedInV3Shell, dispatch } = props;
    const { minDate, maxDate, initialDateRange } = useMemo(() => getTransferDownloadDateWindow(), []);
    const title = i18n.get("transfers.historic.download.title");
    const hintText = config.get("frontend.Transfers.Download.Message");

    const handleBack = useCallback(() => {
        dispatch(goBack());
    }, [dispatch]);

    const handleDownload = useCallback(
        (format, dateFrom, dateTo) => {
            dispatch(
                transferActions.downloadTransfersHistoric(
                    formatTransferDownloadDateLocal(dateFrom),
                    formatTransferDownloadDateLocal(dateTo),
                    format,
                ),
            );
        },
        [dispatch],
    );

    const desktopHeaderMode = useMemo(() => {
        if (isDesktop) {
            return embeddedInV3Shell && isUxDashboardImprovementsEnabled() ? "pageHeader" : "legacyTitle";
        }
        return "none";
    }, [embeddedInV3Shell, isDesktop]);

    useEffect(() => {
        if (!embeddedInV3Shell || !isUxDashboardImprovementsEnabled()) {
            return undefined;
        }
        dispatch(layoutActions.setScopes(["form"]));
        if (!isDesktop) {
            dispatch(layoutActions.setTitleKey("transfers.historic.download.title"));
            dispatch(layoutActions.setGoBackPath(getTransferHistoricPath()));
        }
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        };
    }, [embeddedInV3Shell, isDesktop, dispatch]);

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="form" />}
            {!embeddedInV3Shell && !isDesktop && (
                <Box className="admin-detail-head z-index-1 px-0">
                    <Head
                        onBack={handleBack}
                        headerClassName="blue-main-header-mobile"
                        centerElement={() => (
                            <Text as="subtitle" className="m-0 no-wrap text-white font-medium">
                                {i18n.get("transfers.historic.btn.download")}
                            </Text>
                        )}
                        accessibilityTextId="transfers.historic.download.title"
                    />
                </Box>
            )}
            {!embeddedInV3Shell && isDesktop && (
                <Box className="admin-detail-head px-0" mx={48}>
                    <Head onBack={handleBack} accessibilityTextId="transfers.historic.download.title" />
                </Box>
            )}
            <TransferDownloadPage
                goBack={handleBack}
                title={title}
                hintText={hintText}
                getLabels={getTransferDownloadLabels}
                initialDateRange={initialDateRange}
                minDate={minDate}
                maxDate={maxDate}
                onDownload={handleDownload}
                downloadLoading={fetching}
                desktopHeaderMode={desktopHeaderMode}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
    fetching: transferSelectors.getFetchingDownload(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(TransferDownload);
